<template>
   <div class="item-column grow flex flex-col">
        <div class="column-header flex items-center justify-center pb-5 gap-2">
            <div class="input-pill bg-gray-100 flex rounded-full h-fit py-1 px-2"
                 :class="{ 'bg-red-100': hueError }">
                <input 
                    id="degree-value" 
                    type="number" 
                    min="0" 
                    step="0.001"
                    class="text-right"
                    :value="effectiveHue || 0"
                    @input="handleHueChange"
                ><span>°</span>
               
            </div>
            <div class="input-pill bg-gray-100 flex rounded-full h-fit py-1 px-2"
                 :class="{ 'bg-red-100': multiplierError }">
                <input
                    id="multiplier-value"
                    type="number"
                    min="0"
                    step="0.01"
                    class="text-right"
                    :value="effectiveMultiplier || 1"
                    @input="handleMultiplierChange">
                <span>x</span>
            </div>
        </div>
        <div>
            <input 
                type="range" 
                id="degree-range" 
                min="0" 
                max="360" 
                step="0.001" 
                v-model="effectiveHue"
                @input="handleHueChange"
            >
        </div>
        <div class="stops-wrapper h-auto grow flex flex-col pb-10">
            <ColorSquare 
                v-for="(colorData, index) in lightnessColors"
                :key="index"
                :color-data="colorData.step === 500 ? getBaseColorData(colorData) : colorData"
            />
        </div>
   </div>
</template>

<script>
import ColorSquare from './ColorSquare.vue'
import { store } from '../store.js'
import { computed, ref, watch } from 'vue'
import Color from 'colorjs.io'

export default {
    name: 'ColorColumn',
    components: {
        ColorSquare,
    },
    props: {
        columnIndex: {
            type: Number,
            required: true
        }
    },
    setup(props) {

        const numberOfStops = computed(() => store.numberOfStops)
        
        // Get the hue data for this column
        const hueData = computed(() => {            
            const hueColors = store.getHueColors()
            
            const result = hueColors[props.columnIndex] || null
            return result
        })
        
        // Properties and methods for user-defined overwrites 
        const manualHue = ref(null)
        const manualMultiplier = ref(null)

        const effectiveHue = computed(() => manualHue.value ?? hueData.value?.hue)
        const effectiveMultiplier = computed(() => manualMultiplier.value ?? hueData.value?.multiplier)

        // Properties for errors
        const hueError = ref(false)
        const multiplierError = ref(false)

        // Validation functions
        const isValidHue = (value) => {
            const num = parseFloat(value)
            return !isNaN(num) && num >= 0 && num < 360
        }

        const isValidMultiplier = (value) => {
            const num = Number(value)
            return !isNaN(num) && num >= 0 && num < 100
        }

        // Input event handlers
        const handleHueChange = (event) => {
            const value = event.target.value
            if (value === '' || isValidHue(value)) {
                hueError.value = false
                manualHue.value = value === '' ? null : parseFloat(value)
            } else {
                hueError.value = true
            }
        }

        const handleMultiplierChange = (event) => {
            const value = event.target.value
            if (value === '' || isValidMultiplier(value)) {
                multiplierError.value = false
                manualMultiplier.value = value === '' ? null : parseFloat(value)
            } else {
                multiplierError.value = true
            }
        }

        // Reset overrides when store changes
        watch([() => store.splitType, () => store.multiplierType, () => store.numberOfHues], () => {
            manualHue.value = null
            manualMultiplier.value = null
            hueError.value = false
            multiplierError.value = false
        })

        // Generate lightness variations for this column's hue
        const lightnessColors = computed(() => {
            if (!hueData.value) return []
                
            // Use effective values (manual override or calculated)
            const hue = effectiveHue.value
            
            // Get the actual chroma that was calculated by getChromaForHue
            const adjustedChroma = store.getChromaForHue(hue)
            
            // Generate colors for this column only using effective values
            return store.generateAdvancedLightnessPalette(hue, store.baseColor?.l || 0.5, adjustedChroma)
        })

        // Method to ensure 500-level uses exact hueData values
        const getBaseColorData = (colorData) => {
            if (!hueData.value || colorData.step !== 500) return colorData
            
            // For 500 level, only override chroma and hue, keep the generated lightness
            const perceivedChroma = hueData.value.oklch.c
            
            const correctedOklch = {
                l: colorData.oklch.l,
                c: perceivedChroma, // Use the target chroma
                h: effectiveHue.value,
                alpha: colorData.oklch.alpha
            }
            
            // Generate hex from these exact values
            const correctedColor = new Color('oklch', [correctedOklch.l, correctedOklch.c, correctedOklch.h])
            const correctedHex = correctedColor.to('srgb').toString({ format: 'hex' })
            
            return {
                ...colorData,
                oklch: correctedOklch,
                hue: effectiveHue.value,
                hex: correctedHex,
                cssColor: `oklch(${correctedOklch.l} ${correctedOklch.c} ${correctedOklch.h})`
            }
        }

        watch(lightnessColors, (newColors) => {
            if (newColors && newColors.length > 0 && hueData.value) {
                const columnData = {
                    columnIndex: props.columnIndex,
                    hue: effectiveHue.value,
                    multiplier: effectiveMultiplier.value,
                    colors: newColors,
                    profile: store.getHueProfile(effectiveHue.value),
                    isOverridden: {
                        hue: manualHue.value !== null,
                        multiplier: manualMultiplier.value !== null
                    }
                }
                store.updateColumnColors(props.columnIndex, columnData)
            }
        }, { immediate: true })

        return {
            numberOfStops,
            hueData,
            lightnessColors,
            effectiveMultiplier,
            effectiveHue,
            handleHueChange,
            handleMultiplierChange,
            hueError,
            multiplierError,
            getBaseColorData
        }
    }
}
</script>

<style scoped>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.input-pill input[type=number] {
    -moz-appearance: textfield;
    outline: none;
    font-size: 0.875rem;
    max-width: 4rem;
}

.input-pill input {
    background: transparent;
    cursor: text;
}
</style>